/**
 * channelsGrid.tsx
 */
import Channel from '@shared/classes/channel';
import DateUtils from '@shared/utils/date';
import EpgUtils from '@shared/utils/epg';
import EpgEvent from '@shared/classes/epgEvent';
import MainTemplate from '@components/templates/main/ui/main';
import Rail from '@shared/classes/rail';
import generateEpgFilter from '@shared/utils/generic/generateEpgFilter';
import useEpg from '@shared/components/utils/hooks/useEpg';
import { PAGINATION, THRESHOLD } from '@shared/types/constants/main';
import { EventMap } from '@shared/types/interfaces/event';
import { channelsSelector } from '@shared/store/slices/channels.slice';
import { eventsSelector } from '@shared/store/slices/events.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useState } from 'react';
import {
  selectCurrentParams,
  updateParams,
} from '@shared/store/slices/router.slice';

/**
 * Displays the classic channels page with the rails
 */
export default function ChannelsGrid() {
  const dispatch = useAppDispatch();

  const { favouriteChannels } = useAppSelector(channelsSelector);
  const { mockedEvents } = useAppSelector(eventsSelector);
  const currentParams = useAppSelector(selectCurrentParams);

  // Channel focused on the previous channels view, if any
  const initialChannelId = (currentParams as { channel: Channel })?.channel?.id;
  const channelId =
    (currentParams as { asset: EpgEvent })?.asset?.channel?.id ||
    (currentParams as { channelId: string })?.channelId ||
    initialChannelId ||
    favouriteChannels[0]?.id;

  const [filter] = useState<Rail>(
    (currentParams as { filter: Rail })?.filter || generateEpgFilter()
  );
  const [rails, setRails] = useState<Rail[]>(
    (currentParams as { rails: Rail[] })?.rails || []
  );

  const { complete, error, loading, epg } = useEpg({
    channelId,
    chunkSize: PAGINATION.CHANNELS_GRID.CHUNK_SIZE,
    dateRange:
      currentParams?.category?.date ??
      (filter.data.length
        ? filter.data[
            filter.data.findIndex((item) =>
              DateUtils.isCurrentDate(item?.date?.start, item?.date?.end)
            )
          ]?.date
        : undefined),
    offset: {
      vertical: {
        previous: PAGINATION.CHANNELS_GRID.PREVIOUS_LOAD_OFFSET,
        next: PAGINATION.CHANNELS_GRID.CHUNK_SIZE / 2,
      },
    },
    pageSize: PAGINATION.CHANNELS_GRID.PAGE_SIZE,
    previousEpg: currentParams?.epg as EventMap,
    ready: !!channelId && !!filter,
  });

  useEffect(() => {
    if (loading) return;
    if (!currentParams?.timeline) return;
    const start = currentParams.timeline.start.getTime();
    const end = currentParams.timeline.end.getTime();
    if (!start || !end) return;

    const newRails: Rail[] = [];
    favouriteChannels?.forEach((ch: Channel) => {
      const filteredEvents = EpgUtils.filterRailEvents(epg[ch.id], start, end);
      const filledEvents = EpgUtils.fillRailGaps(
        filteredEvents,
        mockedEvents[ch.id],
        start,
        end
      );
      const rail = EpgUtils.buildRail(filledEvents, ch.id);
      if (rail) newRails.push(rail);
    });

    setRails(newRails);
    dispatch(updateParams({ epg }));
  }, [epg, favouriteChannels, loading, mockedEvents, currentParams?.timeline]);

  return (
    <MainTemplate
      filter={filter}
      initialSelection={initialChannelId}
      rails={rails}
      flags={{
        complete,
        loading: loading && !error,
        filter: { all: true, fixed: true },
        timeline: true,
      }}
      threshold={{
        front: THRESHOLD.CHANNELS_GRID.FRONT,
        rear: THRESHOLD.CHANNELS_GRID.REAR,
      }}
    />
  );
}
